import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { TRADE_META } from '../../lib/capex-utils'
import { useUpdateWorksite } from '../../hooks/useCapexQueries'
import { useAsset, useChildAssets } from '../../hooks/useQueries'
import type { Worksite, TradeCategory, EquipmentCondition } from './types'

interface EditWorksiteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  worksite: Worksite | null
  org: string
  horizonStart: number
  horizonEnd: number
  buildingAssetUid?: string
}

const TRADE_CATEGORIES = Object.keys(TRADE_META).sort(
  (a, b) => TRADE_META[a as TradeCategory].order - TRADE_META[b as TradeCategory].order
) as TradeCategory[]

const CONDITIONS: EquipmentCondition[] = [1, 2, 3, 4, 5]

const NO_ASSET = '__none__'

export function EditWorksiteDialog({
  open,
  onOpenChange,
  worksite,
  org,
  horizonStart,
  horizonEnd,
  buildingAssetUid,
}: EditWorksiteDialogProps) {
  const { t } = useTranslation('capex')

  const [name, setName] = useState('')
  const [sub, setSub] = useState('')
  const [trade, setTrade] = useState<TradeCategory | ''>('')
  const [condition, setCondition] = useState<string>('')
  const [year, setYear] = useState<string>(String(horizonStart))
  const [assetUid, setAssetUid] = useState(buildingAssetUid ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-fill fields from worksite prop
  useEffect(() => {
    if (worksite) {
      setName(worksite.name ?? '')
      setSub(worksite.sub ?? '')
      setTrade((worksite.trade as TradeCategory) ?? '')
      setCondition(worksite.condition != null ? String(worksite.condition) : '')
      setYear(worksite.year != null ? String(worksite.year) : String(horizonStart))
      setAssetUid(worksite.asset_uid ?? buildingAssetUid ?? '')
      setErrors({})
    }
  }, [worksite, horizonStart, buildingAssetUid])

  // Fetch building asset and its children for the selector
  const { data: buildingAsset } = useAsset(org, buildingAssetUid ?? '')
  const { data: childAssets = [] } = useChildAssets(org, buildingAssetUid)

  // Build flat list: building + children
  const assetOptions = useMemo(() => {
    const options: { uid: string; name: string; isBuilding: boolean }[] = []
    if (buildingAsset) {
      const ba = buildingAsset as Record<string, unknown>
      options.push({
        uid: ba.uid as string,
        name: (ba.name as string) || (ba.uid as string),
        isBuilding: true,
      })
    }
    for (const child of childAssets) {
      const c = child as Record<string, unknown>
      options.push({
        uid: c.uid as string,
        name: (c.name as string) || (c.uid as string),
        isBuilding: false,
      })
    }
    return options
  }, [buildingAsset, childAssets])

  const updateMutation = useUpdateWorksite(org)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = t('create_worksite_dialog.error_name_required')
    }
    if (!trade) {
      newErrors.trade = t('create_worksite_dialog.error_trade_required')
    }
    const condNum = Number(condition)
    if (!condition || condNum < 1 || condNum > 5) {
      newErrors.condition = t('create_worksite_dialog.error_condition_required')
    }
    const yearNum = Number(year)
    if (!year || isNaN(yearNum) || yearNum < horizonStart || yearNum > horizonEnd) {
      newErrors.year = t('create_worksite_dialog.error_year_range', {
        start: horizonStart,
        end: horizonEnd,
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate() || !trade || !worksite) return
    try {
      await updateMutation.mutateAsync({
        worksiteUid: worksite.uid!,
        body: {
          name: name.trim(),
          sub: sub.trim() || undefined,
          trade,
          condition: Number(condition),
          year: Number(year),
          asset_uid: assetUid || undefined,
        },
        ifMatch: worksite.updated_at,
      })
      onOpenChange(false)
    } catch {
      // error handled by mutation state
    }
  }

  const handleClose = () => {
    setErrors({})
    onOpenChange(false)
  }

  if (!worksite) return null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent data-testid="edit-worksite-dialog">
        <DialogHeader>
          <DialogTitle>{t('edit_worksite_dialog.title', 'Edit worksite')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-ws-name">{t('create_worksite_dialog.field_name')}</Label>
            <Input
              id="edit-ws-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('create_worksite_dialog.field_name_placeholder')}
              data-testid="edit-worksite-name"
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          {/* Sub-description */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-ws-sub">{t('create_worksite_dialog.field_sub')}</Label>
            <Input
              id="edit-ws-sub"
              value={sub}
              onChange={(e) => setSub(e.target.value)}
              placeholder={t('create_worksite_dialog.field_sub_placeholder')}
              data-testid="edit-worksite-sub"
            />
          </div>

          {/* Trade */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-ws-trade">{t('create_worksite_dialog.field_trade')}</Label>
            <Select value={trade} onValueChange={(v) => setTrade(v as TradeCategory)}>
              <SelectTrigger id="edit-ws-trade" data-testid="edit-worksite-trade">
                <SelectValue placeholder={t('create_worksite_dialog.field_trade_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {TRADE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat} data-testid={`edit-trade-option-${cat}`}>
                    {t(`trade.${cat}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.trade && <p className="text-sm text-destructive">{errors.trade}</p>}
          </div>

          {/* Condition */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-ws-condition">{t('create_worksite_dialog.field_condition')}</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger id="edit-ws-condition" data-testid="edit-worksite-condition">
                <SelectValue placeholder={t('create_worksite_dialog.field_condition_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c} value={String(c)} data-testid={`edit-condition-option-${c}`}>
                    {c} — {t(`condition.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.condition && <p className="text-sm text-destructive">{errors.condition}</p>}
          </div>

          {/* Year */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-ws-year">
              {t('create_worksite_dialog.field_year')} ({horizonStart}–{horizonEnd})
            </Label>
            <Input
              id="edit-ws-year"
              type="number"
              min={horizonStart}
              max={horizonEnd}
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="edit-worksite-year"
            />
            {errors.year && <p className="text-sm text-destructive">{errors.year}</p>}
          </div>

          {/* Asset selection */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-ws-asset">
              {t('create_worksite_dialog.field_asset')}
              <span className="ml-1 text-xs text-muted-foreground">
                ({t('create_worksite_dialog.optional')})
              </span>
            </Label>
            <Select
              value={assetUid || NO_ASSET}
              onValueChange={(v) => setAssetUid(v === NO_ASSET ? '' : v)}
            >
              <SelectTrigger id="edit-ws-asset" data-testid="edit-worksite-asset">
                <SelectValue placeholder={t('create_worksite_dialog.field_asset_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ASSET} data-testid="edit-asset-option-none">
                  {t('create_worksite_dialog.no_asset')}
                </SelectItem>
                {assetOptions.map((opt) => (
                  <SelectItem key={opt.uid} value={opt.uid} data-testid={`edit-asset-option-${opt.uid}`}>
                    {opt.isBuilding ? opt.name : `  └ ${opt.name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('create_worksite_dialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            data-testid="edit-worksite-submit"
          >
            {updateMutation.isPending
              ? t('create_worksite_dialog.submitting')
              : t('edit_worksite_dialog.submit', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
